/**
 * Groups Store
 * 
 * CRUD operations for managing user groups in the system.
 * Groups are used to organize users and assign permissions collectively.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  getCurrentWorkspaceId,
  type SyncState,
} from '../../lib/supabaseSync';

const TABLE_NAME = 'groups';

// ============================================================================
// Types
// ============================================================================

/**
 * Group as stored in the database
 */
export interface Group {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  
  // Parent group for nesting
  parentId?: string;
  
  // Org unit association
  orgUnitId?: string;
  
  // Is this a system group (cannot be deleted)
  isSystem: boolean;
  
  // Workspace scope (groups are local to a workspace; never cross tenants)
  workspaceId?: string;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Group with member count (for display)
 */
export interface GroupWithMemberCount extends Group {
  memberCount: number;
}

/**
 * Input for creating a new group
 */
export interface CreateGroupInput {
  name: string;
  displayName: string;
  description?: string;
  parentId?: string;
  orgUnitId?: string;
  isSystem?: boolean;
}

/**
 * Input for updating an existing group
 */
export interface UpdateGroupInput {
  name?: string;
  displayName?: string;
  description?: string;
  parentId?: string;
  orgUnitId?: string;
}

// ============================================================================
// Store Interface
// ============================================================================

interface GroupsState {
  // Data
  groups: Group[];
  
  // Sync state
  syncState: SyncState;
  
  // CRUD Actions
  fetchFromSupabase: () => Promise<void>;
  createGroup: (input: CreateGroupInput) => Promise<{ success: boolean; group?: Group; error?: string }>;
  updateGroup: (id: string, updates: UpdateGroupInput) => Promise<{ success: boolean; error?: string }>;
  deleteGroup: (id: string) => Promise<{ success: boolean; error?: string }>;
  
  // Getters
  getGroupById: (id: string) => Group | undefined;
  getGroupByName: (name: string) => Group | undefined;
  getChildGroups: (parentId: string) => Group[];
  getRootGroups: () => Group[];
  getGroupHierarchy: (groupId: string) => Group[];
  getSystemGroups: () => Group[];
  getCustomGroups: () => Group[];
  
  // Utilities
  loadDemoData: () => void;
  reset: () => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useGroupsStore = create<GroupsState>()(
  (set, get) => ({
    groups: [],
    syncState: createInitialSyncState(),
      
      // ======================================================================
      // Supabase Sync
      // ======================================================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[GroupsStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const result = await fetchAll<Group>(TABLE_NAME, { column: 'name', ascending: true }, { filterByWorkspace: true, strictWorkspaceFilter: true });
        
        if (result.error) {
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              syncError: result.error 
            } 
          });
          console.error('[GroupsStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        if (result.data) {
          set({ 
            groups: result.data,
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null 
            }
          });
          console.log(`[GroupsStore] Loaded ${result.data.length} groups from Supabase`);
        }
      },
      
      // ======================================================================
      // CRUD Operations
      // ======================================================================
      
      createGroup: async (input) => {
        const workspaceId = getCurrentWorkspaceId();
        const newGroup: Group = {
          id: generateUUID(),
          name: input.name,
          displayName: input.displayName,
          description: input.description,
          parentId: input.parentId,
          orgUnitId: input.orgUnitId,
          isSystem: input.isSystem ?? false,
          workspaceId: workspaceId ?? undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        // Check if group name already exists
        const existing = get().groups.find(g => g.name === input.name);
        if (existing) {
          return { success: false, error: `Group with name "${input.name}" already exists` };
        }
        
        // Validate parent exists if specified
        if (input.parentId) {
          const parent = get().groups.find(g => g.id === input.parentId);
          if (!parent) {
            return { success: false, error: 'Parent group not found' };
          }
        }
        
        // Optimistic update
        set((state) => ({ groups: [...state.groups, newGroup] }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await insertRecord<Group>(TABLE_NAME, newGroup);
          
          if (result.error) {
            // Rollback on error
            set((state) => ({ groups: state.groups.filter(g => g.id !== newGroup.id) }));
            console.error('[GroupsStore] Failed to create group:', result.error);
            return { success: false, error: result.error };
          }
        }
        
        console.log(`[GroupsStore] Created group: ${newGroup.displayName}`);
        return { success: true, group: newGroup };
      },
      
      updateGroup: async (id, updates) => {
        const group = get().groups.find(g => g.id === id);
        if (!group) {
          return { success: false, error: 'Group not found' };
        }
        
        if (group.isSystem && updates.name) {
          return { success: false, error: 'Cannot rename system groups' };
        }
        
        // Check for circular parent reference
        if (updates.parentId) {
          if (updates.parentId === id) {
            return { success: false, error: 'Group cannot be its own parent' };
          }
          
          // Check if new parent is a descendant
          const isDescendant = (parentId: string, checkId: string): boolean => {
            const children = get().groups.filter(g => g.parentId === checkId);
            for (const child of children) {
              if (child.id === parentId) return true;
              if (isDescendant(parentId, child.id)) return true;
            }
            return false;
          };
          
          if (isDescendant(updates.parentId, id)) {
            return { success: false, error: 'Cannot set a descendant group as parent' };
          }
        }
        
        // Check for duplicate name if renaming
        if (updates.name && updates.name !== group.name) {
          const existing = get().groups.find(g => g.name === updates.name && g.id !== id);
          if (existing) {
            return { success: false, error: `Group with name "${updates.name}" already exists` };
          }
        }
        
        const updatedGroup: Group = {
          ...group,
          ...updates,
          updatedAt: new Date(),
        };
        
        // Optimistic update
        set((state) => ({
          groups: state.groups.map(g => g.id === id ? updatedGroup : g),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await updateRecord<Group>(TABLE_NAME, id, updatedGroup);
          
          if (result.error) {
            // Rollback on error
            set((state) => ({
              groups: state.groups.map(g => g.id === id ? group : g),
            }));
            console.error('[GroupsStore] Failed to update group:', result.error);
            return { success: false, error: result.error };
          }
        }
        
        console.log(`[GroupsStore] Updated group: ${updatedGroup.displayName}`);
        return { success: true };
      },
      
      deleteGroup: async (id) => {
        const group = get().groups.find(g => g.id === id);
        if (!group) {
          return { success: false, error: 'Group not found' };
        }
        
        if (group.isSystem) {
          return { success: false, error: 'Cannot delete system groups' };
        }
        
        // Check for child groups
        const children = get().groups.filter(g => g.parentId === id);
        if (children.length > 0) {
          return { success: false, error: 'Cannot delete group with child groups. Delete or reassign children first.' };
        }
        
        // Optimistic update
        set((state) => ({
          groups: state.groups.filter(g => g.id !== id),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await deleteRecord(TABLE_NAME, id);
          
          if (result.error) {
            // Rollback on error
            set((state) => ({
              groups: [...state.groups, group],
            }));
            console.error('[GroupsStore] Failed to delete group:', result.error);
            return { success: false, error: result.error };
          }
        }
        
        console.log(`[GroupsStore] Deleted group: ${group.displayName}`);
        return { success: true };
      },
      
      // ======================================================================
      // Getters
      // ======================================================================
      
      getGroupById: (id) => {
        return get().groups.find(g => g.id === id);
      },
      
      getGroupByName: (name) => {
        return get().groups.find(g => g.name === name);
      },
      
      getChildGroups: (parentId) => {
        return get().groups.filter(g => g.parentId === parentId);
      },
      
      getRootGroups: () => {
        return get().groups.filter(g => !g.parentId);
      },
      
      getGroupHierarchy: (groupId) => {
        const hierarchy: Group[] = [];
        let current = get().groups.find(g => g.id === groupId);
        
        while (current) {
          hierarchy.unshift(current);
          current = current.parentId 
            ? get().groups.find(g => g.id === current!.parentId)
            : undefined;
        }
        
        return hierarchy;
      },
      
      getSystemGroups: () => {
        return get().groups.filter(g => g.isSystem);
      },
      
      getCustomGroups: () => {
        return get().groups.filter(g => !g.isSystem);
      },
      
      // ======================================================================
      // Demo Data
      // ======================================================================
      
      loadDemoData: () => {
        const now = new Date();
        
        const demoGroups: Group[] = [
          {
            id: 'group-leadership',
            name: 'leadership',
            displayName: 'Leadership Team',
            description: 'Executive and director-level leadership',
            isSystem: false,
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'group-product',
            name: 'product',
            displayName: 'Product Team',
            description: 'Product managers and designers',
            isSystem: false,
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'group-engineering',
            name: 'engineering',
            displayName: 'Engineering Team',
            description: 'Software engineers and architects',
            isSystem: false,
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'group-customer-success',
            name: 'customer-success',
            displayName: 'Customer Success',
            description: 'Customer success managers and support',
            isSystem: false,
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'group-all-employees',
            name: 'all-employees',
            displayName: 'All Employees',
            description: 'All company employees',
            isSystem: true,
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'group-admins',
            name: 'admins',
            displayName: 'Platform Administrators',
            description: 'Users with full system access',
            isSystem: true,
            createdAt: now,
            updatedAt: now,
          },
        ];
        
        set({ groups: demoGroups });
        console.log('[GroupsStore] Loaded demo data with 6 groups');
      },
      
      reset: () => {
      set({
        groups: [],
        syncState: createInitialSyncState(),
      });
    },
  })
);

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Get all groups
 */
export const useGroups = () => useGroupsStore((state) => state.groups);

/**
 * Get root-level groups (no parent)
 */
export const useRootGroups = () => useGroupsStore((state) => state.getRootGroups());

/**
 * Get system groups only
 */
export const useSystemGroups = () => useGroupsStore((state) => state.getSystemGroups());

/**
 * Get custom (non-system) groups only
 */
export const useCustomGroups = () => useGroupsStore((state) => state.getCustomGroups());

/**
 * Get a specific group by ID
 */
export const useGroupById = (id: string) => 
  useGroupsStore((state) => state.groups.find(g => g.id === id));

/**
 * Get a specific group by name
 */
export const useGroupByName = (name: string) => 
  useGroupsStore((state) => state.groups.find(g => g.name === name));

/**
 * Get child groups of a parent
 */
export const useChildGroups = (parentId: string) => 
  useGroupsStore((state) => state.groups.filter(g => g.parentId === parentId));

/**
 * Get sync state
 */
export const useGroupsSyncState = () => useGroupsStore((state) => state.syncState);
